import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  ExpandMore,
  Security,
  PersonAdd,
  Assignment,
  CheckCircle,
  Cancel as CancelIcon
} from '@mui/icons-material';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: '',
    permissions: [],
    isSystem: false,
    estActif: true
  });

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignData, setAssignData] = useState({
    userId: '',
    roles: [],
    permissions: []
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes, usersRes] = await Promise.all([
        axios.get('/api/roles', { headers: getAuthHeaders() }),
        axios.get('/api/roles/permissions/all', { headers: getAuthHeaders() }),
        axios.get('/api/users', { headers: getAuthHeaders() })
      ]);
      setRoles(rolesRes.data || []);
      setPermissions(permissionsRes.data || []);
      setUsers(usersRes.data || []);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = roles.filter(role => {
    const search = searchTerm.toLowerCase();
    return (role.nom || '').toLowerCase().includes(search) ||
           (role.code || '').toLowerCase().includes(search) ||
           (role.description || '').toLowerCase().includes(search);
  });

  const getPermissionsByCategorie = () => {
    const grouped = {};
    permissions.forEach(p => {
      if (!grouped[p.categorie]) grouped[p.categorie] = [];
      grouped[p.categorie].push(p);
    });
    return grouped;
  };

  const handleTogglePermission = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleOpenModal = (mode, role = null) => {
    setModalMode(mode);
    if (role) {
      setSelectedRole(role);
      setFormData({
        nom: role.nom || '',
        code: role.code || '',
        description: role.description || '',
        permissions: role.permissions?.map(p => p._id || p) || [],
        isSystem: role.isSystem || false,
        estActif: role.estActif !== undefined ? role.estActif : true
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRole(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      code: '',
      description: '',
      permissions: [],
      isSystem: false,
      estActif: true
    });
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'add') {
        await axios.post('/api/roles', formData, { headers: getAuthHeaders() });
        setSuccessMessage('Rôle créé avec succès');
      } else {
        await axios.put(`/api/roles/${selectedRole._id}`, formData, { headers: getAuthHeaders() });
        setSuccessMessage('Rôle modifié avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Supprimer définitivement le rôle ${role.nom} ?`)) return;
    try {
      await axios.delete(`/api/roles/${role._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Rôle supprimé avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setAssignData({
      userId: user._id,
      roles: user.roles?.map(r => r._id || r) || [],
      permissions: user.permissions?.map(p => p._id || p) || []
    });
    setAssignModalOpen(true);
  };

  const handleAssignSubmit = async () => {
    try {
      await axios.put(
        `/api/roles/user/${assignData.userId}/assign`,
        { roles: assignData.roles, permissions: assignData.permissions },
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Rôles assignés avec succès');
      setAssignModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'assignation');
    }
  };

  const getRoleBadgeColor = (code) => {
    const colors = {
      'ADMIN': '#ef4444',
      'DIRECTEUR': '#6d28d9',
      'SCOLARITE': '#0891b2',
      'CHEF_FILIERE': '#0f766e',
      'DIRECTION_FINANCIERE': '#b45309',
      'DIRECTION_GENERALE': '#6366f1',
      'COORDINATEUR_PEDAGOGIQUE': '#0d9488',
      'ENSEIGNANT': '#3b82f6',
      'ETUDIANT': '#10b981',
      'CONSULTANT': '#f59e0b',
      'RESPONSABLE': '#8b5cf6',
      'FINANCE': '#06b6d4',
      'SECRETARIAT': '#ec489a'
    };
    return colors[code] || '#6b7280';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const groupedPermissions = getPermissionsByCategorie();

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            🛡️ Gestion des Rôles et Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} rôle(s) trouvé(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouveau Rôle
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, code ou description..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Rôle</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Système</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((role) => (
              <TableRow key={role._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: getRoleBadgeColor(role.code),
                      display: 'inline-block'
                    }} />
                    <Typography fontWeight={500}>{role.nom}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={role.code} size="small" sx={{ fontFamily: 'monospace' }} />
                </TableCell>
                <TableCell>{role.description || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={`${role.permissions?.length || 0} permissions`}
                    size="small"
                    sx={{ bgcolor: '#dbeafe', color: '#1e40af' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={role.isSystem ? '✅ Système' : '📝 Personnalisé'}
                    size="small"
                    sx={{ bgcolor: role.isSystem ? '#dcfce7' : '#fef3c7', color: role.isSystem ? '#166534' : '#92400e' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={role.estActif ? '✅ Actif' : '❌ Inactif'}
                    size="small"
                    sx={{ bgcolor: role.estActif ? '#dcfce7' : '#fee2e2', color: role.estActif ? '#166534' : '#991b1b' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Modifier">
                      <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', role)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!role.isSystem && (
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(role)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun rôle trouvé</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* Modal Ajout/Modification Rôle */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter un rôle' : '✏️ Modifier le rôle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estActif}
                      onChange={(e) => setFormData({ ...formData, estActif: e.target.checked })}
                    />
                  }
                  label={formData.estActif ? '✅ Rôle actif' : '❌ Rôle inactif'}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Permissions</Typography>

            {Object.entries(groupedPermissions).map(([categorie, perms]) => (
              <Accordion key={categorie} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={500}>
                    {categorie.charAt(0).toUpperCase() + categorie.slice(1).toLowerCase()}
                    <Chip
                      label={`${perms.filter(p => formData.permissions.includes(p._id)).length}/${perms.length}`}
                      size="small"
                      sx={{ ml: 1, bgcolor: '#dbeafe', color: '#1e40af' }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    {perms.map((p) => (
                      <Grid item xs={12} sm={6} md={4} key={p._id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.permissions.includes(p._id)}
                              onChange={() => handleTogglePermission(p._id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{p.nom}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.code}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Créer' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Assignation des rôles aux utilisateurs */}
      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment color="primary" />
            Assigner des rôles à {selectedUser?.nom} {selectedUser?.prenom}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Rôles disponibles</Typography>
            <Grid container spacing={1}>
              {roles.map((role) => (
                <Grid item xs={12} sm={6} key={role._id}>
                  <Card sx={{ 
                    borderRadius: 2, 
                    border: assignData.roles.includes(role._id) ? '2px solid #667eea' : '1px solid #e5e7eb',
                    bgcolor: assignData.roles.includes(role._id) ? '#f3f0ff' : 'white'
                  }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={assignData.roles.includes(role._id)}
                            onChange={() => {
                              setAssignData(prev => ({
                                ...prev,
                                roles: prev.roles.includes(role._id)
                                  ? prev.roles.filter(id => id !== role._id)
                                  : [...prev.roles, role._id]
                              }));
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{role.nom}</Typography>
                            <Typography variant="caption" color="text.secondary">{role.code}</Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={`${role.permissions?.length || 0} permissions`}
                          size="small"
                          sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>Annuler</Button>
          <Button onClick={handleAssignSubmit} variant="contained" sx={{ bgcolor: '#667eea' }}>
            Assigner
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;


